import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import TimeInput12h from '../ui/time-input-12h';
import useBranchLabels from '../../hooks/useBranchLabels';

const emptyRow = () => ({
  subject_id: '',
  exam_date: '',
  start_time: '09:00',
  end_time: '12:00',
  max_marks: 100,
  passing_marks: 35,
  room: '',
});

const ExamCreateWizard = ({ open, onOpenChange, onComplete }) => {
  const { user } = useAuth();
  const { formatLabel } = useBranchLabels();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [examId, setExamId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [formData, setFormData] = useState({
    name: '', description: '', start_date: '', end_date: '', exam_type: 'term',
  });
  const [classId, setClassId] = useState('');
  const [rows, setRows] = useState([emptyRow()]);

  const examTypes = [
    { value: 'term', label: 'Term Exam' },
    { value: 'mid_term', label: 'Mid-Term' },
    { value: 'final', label: 'Final Exam' },
    { value: 'unit_test', label: 'Unit Test' },
    { value: 'other', label: 'Other' },
  ];

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    if (!open) {
      setStep(1);
      setExamId(null);
      setFormData({ name: '', description: '', start_date: '', end_date: '', exam_type: 'term' });
      setClassId('');
      setRows([emptyRow()]);
      return;
    }
    fetchClasses();
  }, [open, user?.school_id]);

  useEffect(() => {
    if (!classId) {
      setSubjects([]);
      return;
    }
    const cls = classes.find((c) => String(c.id) === String(classId));
    const classSubjects = cls?.subjects || [];
    if (Array.isArray(classSubjects) && classSubjects.length > 0) {
      setSubjects(classSubjects);
      return;
    }
    fetchSubjectsFallback();
  }, [classId, classes]);

  const fetchClasses = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const res = await api.get('/classes', { params });
      const list = res.data.data || [];
      setClasses(list);
      if (!classId && list.length > 0) {
        setClassId(String(list[0].id));
      }
    } catch {
      setClasses([]);
    }
  };

  const fetchSubjectsFallback = async () => {
    try {
      const res = await api.get('/subjects');
      setSubjects(res.data.data || []);
    } catch {
      setSubjects([]);
    }
  };

  const handleStep1Next = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const res = await api.post('/exams', {
        ...formData,
        ...(schoolId ? { school_id: schoolId } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      const id = res.data?.data?.id;
      setExamId(id);
      setStep(2);
      toast({ title: 'Exam created', description: 'Now add the datesheet for a class.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to create exam', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDatesheet = async () => {
    if (!examId || !classId) {
      toast({ title: 'Error', description: 'Select a class and add schedule rows', variant: 'destructive' });
      return;
    }
    const schedules = rows.filter((r) => r.subject_id && r.exam_date);
    if (schedules.length === 0) {
      toast({ title: 'Error', description: 'Add at least one subject with date', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/exams/${examId}/datesheets`, {
        class_id: Number(classId),
        schedules: schedules.map((r) => ({
          subject_id: Number(r.subject_id),
          exam_date: r.exam_date,
          start_time: r.start_time ? `${r.start_time}:00` : '09:00:00',
          end_time: r.end_time ? `${r.end_time}:00` : '12:00:00',
          max_marks: Number(r.max_marks) || 100,
          passing_marks: Number(r.passing_marks) || 35,
          room: r.room || null,
        })),
      });
      toast({ title: 'Success', description: 'Datesheet saved as draft' });
      onOpenChange(false);
      onComplete?.();
      navigate(`/exams/${examId}`);
    } catch {
      toast({ title: 'Error', description: 'Failed to save datesheet', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
    onComplete?.();
    if (examId) navigate(`/exams/${examId}`);
  };

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Exam — Step {step} of 2</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <div className={`flex-1 h-1 rounded ${step >= 1 ? 'bg-blue-600' : 'bg-muted'}`} />
          <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-blue-600' : 'bg-muted'}`} />
        </div>

        {step === 1 && (
          <form onSubmit={handleStep1Next} className="space-y-4">
            <div>
              <Label>Exam Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-muted border-border" required />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-muted border-border" />
            </div>
            <div>
              <Label>Exam Type</Label>
              <Select value={formData.exam_type} onValueChange={(v) => setFormData({ ...formData, exam_type: v })}>
                <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {examTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <DateInput value={formData.start_date} onChange={(v) => setFormData({ ...formData, start_date: v })}
                  className="bg-muted border-border" required />
              </div>
              <div>
                <Label>End Date *</Label>
                <DateInput value={formData.end_date} onChange={(v) => setFormData({ ...formData, end_date: v })}
                  className="bg-muted border-border" required />
              </div>
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={submitting}>
              {submitting ? 'Creating…' : 'Next — Add Datesheet'}
            </Button>
          </form>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label>Class *</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {classes.map((c) => <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Subject schedule rows</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setRows((p) => [...p, emptyRow()])}>
                  <Plus className="w-4 h-4 mr-1" /> Add row
                </Button>
              </div>
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end border border-border rounded-lg p-3"
                >
                  <div className="sm:col-span-4">
                    <Label className="text-xs">Subject</Label>
                    <Select value={row.subject_id} onValueChange={(v) => updateRow(idx, 'subject_id', v)}>
                      <SelectTrigger className="bg-muted border-border h-9"><SelectValue placeholder="Subject" /></SelectTrigger>
                      <SelectContent className="bg-muted border-border">
                        {subjects.map((s) => <SelectItem key={s.id} value={String(s.id)}>{formatLabel(s.name, s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-3">
                    <Label className="text-xs">Date</Label>
                    <DateInput value={row.exam_date} onChange={(v) => updateRow(idx, 'exam_date', v)}
                      className="bg-muted border-border h-9 w-full" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Start</Label>
                    <TimeInput12h value={row.start_time} onChange={(v) => updateRow(idx, 'start_time', v)} triggerClassName="bg-muted border-border h-9" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">End</Label>
                    <TimeInput12h value={row.end_time} onChange={(v) => updateRow(idx, 'end_time', v)} triggerClassName="bg-muted border-border h-9" />
                  </div>
                  <div className="hidden sm:block sm:col-span-1" />

                  <div className="sm:col-span-2">
                    <Label className="text-xs">Max</Label>
                    <Input type="number" value={row.max_marks} onChange={(e) => updateRow(idx, 'max_marks', e.target.value)}
                      className="bg-muted border-border h-9 w-full text-center tabular-nums" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Pass</Label>
                    <Input type="number" value={row.passing_marks} onChange={(e) => updateRow(idx, 'passing_marks', e.target.value)}
                      className="bg-muted border-border h-9 w-full text-center tabular-nums" />
                  </div>
                  <div className="sm:col-span-7">
                    <Label className="text-xs">Room</Label>
                    <Input value={row.room} onChange={(e) => updateRow(idx, 'room', e.target.value)} className="bg-muted border-border h-9 w-full" />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    {rows.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => setRows((p) => p.filter((_, i) => i !== idx))}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)} disabled={submitting}>Back</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={handleSkip} disabled={submitting}>Skip for now</Button>
              <Button type="button" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSaveDatesheet} disabled={submitting}>
                {submitting ? 'Saving…' : 'Save Datesheet'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExamCreateWizard;
